import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import TasksLayout from "../../components/TaskDetailsLayouts/TasksLayout";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { nanoid } from 'nanoid';
import apiClient from "../../api/apiClient";
import WordCounter from "../../components/tasks/WordCounter";
import TaskProgressIndicator from "../../components/tasks/TaskProgressIndicator";
import SectionCard from "../../components/tasks/SectionCard";
import { FiEye, FiEdit3 } from "react-icons/fi";

// Main component
const AttempterTaskDetails = () => {
  // State for sections
  const [currentStep, setCurrentStep] = useState(0);
  const [isRtl, setIsRtl] = useState(false);
  const [activeSection, setActiveSection] = useState('prompt');
  
  // Markdown preview toggle states
  const [promptMarkdownPreview, setPromptMarkdownPreview] = useState(false);
  const [responseMarkdownPreview, setResponseMarkdownPreview] = useState(false);
  
  // Form state
  const [prompt, setPrompt] = useState("");
  const [promptSaved, setPromptSaved] = useState("");
  const [promptHasChanges, setPromptHasChanges] = useState(false);
  const [promptComplete, setPromptComplete] = useState(false);
  
  const [response, setResponse] = useState("");
  const [responseSaved, setResponseSaved] = useState("");
  const [responseHasChanges, setResponseHasChanges] = useState(false);
  const [responseComplete, setResponseComplete] = useState(false);
  
  const [language, setLanguage] = useState("");
  const [languageSaved, setLanguageSaved] = useState("");
  const [taskCategory, setTaskCategory] = useState("");
  const [taskCategorySaved, setTaskCategorySaved] = useState("");
  const [technicalCategory, setTechnicalCategory] = useState("");
  const [technicalCategorySaved, setTechnicalCategorySaved] = useState("");
  const [metadataHasChanges, setMetadataHasChanges] = useState(false);
  const [metadataComplete, setMetadataComplete] = useState(false);
  const [isArabicMSA, setIsArabicMSA] = useState(false);
  

  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic lists for selections
  const languageOptions = [
    { display: "Python", value: "python" },
    { display: "JavaScript", value: "javascript" },
    { display: "Java", value: "java" },
    { display: "Dart", value: "dart" },
    { display: "PHP", value: "php" },
    { display: "Ruby", value: "ruby" },
    { display: "Rust", value: "rust" },
    { display: "Kotlin", value: "kotlin" },
    { display: "Scala", value: "scala" },
    { display: "Erlang", value: "erlang" },
    { display: "Elixir", value: "elixir" },
    { display: "C#", value: "csharp" },
    { display: "Swift", value: "swift" },
    { display: "Go", value: "go" },
    { display: "C++", value: "c++" },
    { display: "C", value: "c" },
    { display: "TypeScript", value: "typescript" }
  ];
  
  const taskCategoryOptions = [
    { display: "Tourism", value: "tourism" },
    { display: "Sports", value: "sports" },
    { display: "Games", value: "games" },
    { display: "Health", value: "health" },
    { display: "Finance", value: "finance" }
  ];

  const technicalCategoryOptions = [
    { display: "Code Debugging", value: "debugging" },
    { display: "Code Generation", value: "generation" },
    { display: "Code Understanding", value: "understanding" }
  ];

  // Load saved data from localStorage on mount
  useEffect(() => {
    try {
      const savedTask = localStorage.getItem(`task_draft`);
      if (savedTask) {
        const taskData = JSON.parse(savedTask);
        
        if (taskData.prompt) {
          setPrompt(taskData.prompt);
          setPromptSaved(taskData.prompt);
          setPromptComplete(true);
          setCurrentStep(1);
        }
        
        if (taskData.response) {
          setResponse(taskData.response);
          setResponseSaved(taskData.response);
          setResponseComplete(true);
          setCurrentStep(2);
        }
        
        if (taskData.language && taskData.taskCategory) {
          setLanguage(taskData.language);
          setLanguageSaved(taskData.language);
          setTaskCategory(taskData.taskCategory);
          setTaskCategorySaved(taskData.taskCategory);
          if (taskData.technicalCategory) {
            setTechnicalCategory(taskData.technicalCategory);
            setTechnicalCategorySaved(taskData.technicalCategory);
          }
          setMetadataComplete(true);
        }
        
        if (taskData.isRtl) {
          setIsRtl(taskData.isRtl);
        }
        
        if (taskData.isArabicMSA !== undefined) {
          setIsArabicMSA(taskData.isArabicMSA);
        }
      }
    } catch (err) {
      console.error("Error loading saved task:", err);
    }
  }, []);

  // Auto-select the active section based on saved progress
  useEffect(() => {
    if (!promptComplete) {
      setActiveSection("prompt");
    } else if (!responseComplete) {
      setActiveSection("response");
    } else if (!metadataComplete) {
      setActiveSection("metadata");
    }
  }, [promptComplete, responseComplete, metadataComplete]);

  // Track changes for each section
  useEffect(() => {
    setPromptHasChanges(prompt !== promptSaved);
  }, [prompt, promptSaved]);
  
  useEffect(() => {
    setResponseHasChanges(response !== responseSaved);
  }, [response, responseSaved]);
  
  useEffect(() => {
    setMetadataHasChanges(
      language !== languageSaved || 
      taskCategory !== taskCategorySaved || 
      technicalCategory !== technicalCategorySaved
    );
  }, [language, languageSaved, taskCategory, taskCategorySaved, technicalCategory, technicalCategorySaved]);

  // Word count function
  const wordCount = (text) => {
    return text.trim().split(/\s+/).length;
  };

  // Validation functions
  const validatePrompt = () => {
    return prompt.trim() && wordCount(prompt) >= 60;
  };
  
  const validateResponse = () => {
    return response.trim() && wordCount(response) >= 20;
  };
  
  const validateMetadata = () => {
    return language && taskCategory && technicalCategory;
  };

  // Save functions for each section
  const savePrompt = () => {
    if (validatePrompt()) {
      setPromptSaved(prompt);
      setPromptHasChanges(false);
      setPromptComplete(true);
      setActiveSection('response');
      setCurrentStep(Math.max(currentStep, 1));
      saveToLocalStorage();
    }
  };
  
  const saveResponse = () => {
    if (validateResponse()) {
      setResponseSaved(response);
      setResponseHasChanges(false);
      setResponseComplete(true);
      setActiveSection('metadata');
      setCurrentStep(Math.max(currentStep, 2));
      saveToLocalStorage();
    }
  };
  
  const saveMetadata = () => {
    if (validateMetadata()) {
      setLanguageSaved(language);
      setTaskCategorySaved(taskCategory);
      setTechnicalCategorySaved(technicalCategory);
      setMetadataHasChanges(false);
      setMetadataComplete(true);
      saveToLocalStorage();
    }
  };

  // Helper function to save data to localStorage
  const saveToLocalStorage = () => {
    try {
      const taskData = {
        prompt,
        response,
        language,
        taskCategory,
        technicalCategory,
        isRtl,
        isArabicMSA,
        lastUpdated: new Date().toISOString(),
      };
      localStorage.setItem(`task_draft`, JSON.stringify(taskData));
    } catch (err) {
      console.error("Error saving task:", err);
    }
  };

  // Toast notification
  const showToast = (message, type = 'success') => {
    Swal.fire({
      icon: type,
      title: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  // Full task validation
  const validateInputs = () => {
    let errors = [];
    if (!validatePrompt()) {
      errors.push("- Prompt is required and must be at least 60 words.");
    }
    if (!validateResponse()) {
      errors.push("- Response is required and must be at least 20 words.");
    }
    if (!language) {
      errors.push("- Language is required.");
    }
    if (!taskCategory) {
      errors.push("- Task Category is required.");
    }
    if (!technicalCategory) {
      errors.push("- Technical Category is required.");
    }
    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Please Fix These Issues",
        html: `<ul class="text-left">${errors.map((error) => `<li>${error}</li>`).join("")}</ul>`,
        confirmButtonColor: '#10b981',
      });
      return false;
    }
    return true;
  };
  
  // Redux state
  const { token } = useSelector((state) => state.auth);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  
  // Submit the complete task
  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (!validateInputs()) {
      setIsSubmitting(false);
      return;
    }
    
    try {
      const apiResponse = await apiClient.post("/api/tasks/tasks/", {
          prompt,
          response,
          language: language.toLowerCase(),
          category: taskCategory.toLowerCase(),
          technical_category: technicalCategory || null,
          is_arabic_msa: isArabicMSA,
      });

      if (apiResponse.status === 201) {
        localStorage.removeItem(`task_draft`);
        navigate("/submitted");
      }
    } catch (error) {
      console.error("An error occurred while submitting the task", error);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.response?.data?.message || "An unexpected error occurred. Please try again.",
        confirmButtonColor: "#10b981",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Markdown components for rendering
  const markdownComponents = {
    h1: ({ node, ...props }) => <h1 className="text-xl font-bold my-4 text-gray-800 dark:text-gray-200" {...props} />,
    h2: ({ node, ...props }) => <h2 className="text-lg font-bold my-3 text-gray-800 dark:text-gray-200" {...props} />,
    h3: ({ node, ...props }) => <h3 className="text-md font-bold my-2 text-gray-800 dark:text-gray-200" {...props} />,
    h4: ({ node, ...props }) => <h4 className="text-base font-bold my-2 text-gray-800 dark:text-gray-200" {...props} />,
    h5: ({ node, ...props }) => <h5 className="text-sm font-bold my-1 text-gray-800 dark:text-gray-200" {...props} />,
    h6: ({ node, ...props }) => <h6 className="text-xs font-bold my-1 text-gray-800 dark:text-gray-200" {...props} />,
    p: ({ node, ...props }) => <p className="my-2 text-gray-800 dark:text-gray-200" {...props} />,
    a: ({ node, ...props }) => <a className="text-blue-600 hover:underline dark:text-blue-400" {...props} />,
    ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 text-gray-800 dark:text-gray-200" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 text-gray-800 dark:text-gray-200" {...props} />,
    li: ({ node, ...props }) => <li className="my-1 text-gray-800 dark:text-gray-200" {...props} />,
    blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-700 pl-4 italic my-2 text-gray-700 dark:text-gray-300" {...props} />,
    table: ({ node, ...props }) => <div className="overflow-x-auto my-2"><table className="min-w-full border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200" {...props} /></div>,
    thead: ({ node, ...props }) => <thead className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200" {...props} />,
    tbody: ({ node, ...props }) => <tbody className="divide-y divide-gray-300 dark:divide-gray-700 text-gray-800 dark:text-gray-200" {...props} />,
    tr: ({ node, ...props }) => <tr className="hover:bg-gray-100 dark:hover:bg-gray-750 text-gray-800 dark:text-gray-200" {...props} />,
    th: ({ node, ...props }) => <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r last:border-r-0 border-gray-300 dark:border-gray-700" {...props} />,
    td: ({ node, ...props }) => <td className="px-3 py-2 whitespace-normal text-sm border-r last:border-r-0 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200" {...props} />,
    hr: ({ node, ...props }) => <hr className="my-4 border-t border-gray-300 dark:border-gray-700" {...props} />,
    img: ({ node, ...props }) => <img className="max-w-full h-auto my-2 rounded" {...props} />,
    pre: ({ node, ...props }) => <pre className="rounded-md my-2 bg-gray-800 text-white overflow-x-auto" {...props} />,
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <div className="overflow-x-auto rounded-md my-2">
          <SyntaxHighlighter
            style={atomDark}
            showLineNumbers={true}
            language={match[1]}
            PreTag="div"
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        </div>
      ) : (
        <code className={`${inline ? 'px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' : ''} ${className || ''}`} {...props}>
          {children}
        </code>
      );
    }
  };

  return (
    <TasksLayout handleSubmit={handleSubmit}>
      <div className="p-4 pt-1 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
        <div className="mx-auto">
          <div className="mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                  {isRtl ? "إنشاء مهمة جديدة" : "Create New Task"}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  {isRtl ? "اتبع الخطوات التالية لإنشاء مهمتك" : "Follow the steps below to create your task"}
                </p>
              </div>
              {/* RTL Toggle Only */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="rtl-toggle" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {isRtl ? "وضع RTL" : "RTL Mode"}
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      id="rtl-toggle"
                      checked={isRtl}
                      onChange={() => setIsRtl(!isRtl)}
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 dark:peer-focus:ring-cyan-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-cyan-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Task Creation Tips */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                <svg className="w-5 h-5 mr-2 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {isRtl ? "نصائح لإنشاء المهمة" : "Task Creation Tips"}
              </h2>
            </div>
            
            <div className="p-6">
              {/* Step Indicator */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-full flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep >= 0 ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-gray-300 dark:border-gray-600'}`}>
                      <span className={`text-sm font-medium ${currentStep >= 0 ? 'text-cyan-500 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>1</span>
                    </div>
                    <div className={`h-1 flex-1 mx-2 ${currentStep >= 1 ? 'bg-cyan-500' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep >= 1 ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-gray-300 dark:border-gray-600'}`}>
                      <span className={`text-sm font-medium ${currentStep >= 1 ? 'text-cyan-500 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>2</span>
                    </div>
                    <div className={`h-1 flex-1 mx-2 ${currentStep >= 2 ? 'bg-cyan-500' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep >= 2 ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-gray-300 dark:border-gray-600'}`}>
                      <span className={`text-sm font-medium ${currentStep >= 2 ? 'text-cyan-500 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>3</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 px-1">
                  <span className={`${currentStep === 0 ? 'font-semibold text-cyan-600 dark:text-cyan-400' : ''}`}>{isRtl ? "نص المهمة" : "Task Prompt"}</span>
                  <span className={`${currentStep === 1 ? 'font-semibold text-cyan-600 dark:text-cyan-400' : ''}`}>{isRtl ? "الاستجابة المتوقعة" : "Expected Response"}</span>
                  <span className={`${currentStep === 2 ? 'font-semibold text-cyan-600 dark:text-cyan-400' : ''}`}>{isRtl ? "تفاصيل المهمة" : "Task Details"}</span>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className={`flex items-start space-x-3 p-4 rounded-lg transition-colors ${currentStep === 0 ? 'bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800' : ''}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 0 ? 'bg-cyan-500 text-white' : 'bg-cyan-100 dark:bg-cyan-900 text-cyan-600 dark:text-cyan-300'}`}>
                    <span className="font-semibold">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">{isRtl ? "اكتب نصًا واضحًا" : "Write a Clear Prompt"}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                      {isRtl 
                        ? "اجعل النص محددًا ومفصلاً. قم بتضمين أي سياق وتعليمات ضرورية. اهدف إلى 60 كلمة على الأقل لضمان الوضوح."
                        : "Make your prompt specific and detailed. Include any necessary context and instructions. Aim for at least 60 words to ensure clarity."
                      }
                    </p>
                  </div>
                </div>
                
                <div className={`flex items-start space-x-3 p-4 rounded-lg transition-colors ${currentStep === 1 ? 'bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800' : ''}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 1 ? 'bg-cyan-500 text-white' : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-300'}`}>
                    <span className="font-semibold">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">{isRtl ? "قدم الاستجابة المتوقعة" : "Provide Expected Response"}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                      {isRtl 
                        ? "فصّل الاستجابة المتوقعة للنص."
                        : "Detail the expected response to the prompt. "
                      }
                    </p>
                  </div>
                </div>
                
                <div className={`flex items-start space-x-3 p-4 rounded-lg transition-colors ${currentStep === 2 ? 'bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800' : ''}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 2 ? 'bg-cyan-500 text-white' : 'bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-300'}`}>
                    <span className="font-semibold">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">{isRtl ? "أضف تفاصيل المهمة" : "Add Task Details"}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                      {isRtl 
                        ? "حدد لغة البرمجة والفئة المناسبة."
                        : "Select the appropriate programming language and category. "
                      }
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center text-amber-600 dark:text-amber-400 mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="font-medium">{isRtl ? "نصيحة احترافية" : "Pro Tip"}</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm" dir={isRtl ? "rtl" : "ltr"}>
                    {isRtl 
                      ? "يمكنك استخدام Markdown في حقول النص والاستجابة للتنسيق. جرب استخدام علامات الاقتباس للكود (```language)، **غامق** للتأكيد، وميزات Markdown الأخرى."
                      : "You can use Markdown in both prompt and response fields for formatting. Try using backticks for code blocks (```language), **bold** for emphasis, and other Markdown features."
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Prompt Section */}
          <SectionCard 
            title={isRtl ? "نص المهمة" : "Task Prompt"}
            isComplete={promptComplete}
            isActive={activeSection === 'prompt'}
            onActivate={() => setActiveSection('prompt')}
            onSave={savePrompt}
            canSave={validatePrompt()}
            hasChanges={promptHasChanges}
            isRtl={isRtl}
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isRtl 
                    ? <>اكتب وصفًا تفصيليًا <span className="text-sm text-red-500">(الحد الأدنى 60 كلمة)</span></> 
                    : <>Write a detailed prompt <span className="text-sm text-red-500">(minimum 60 words)</span></>
                  }
                </label>
                <div className="flex items-center gap-4">
                  <WordCounter text={prompt} minWords={60} />
                  {/* Markdown Preview Toggle */}
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={promptMarkdownPreview}
                        onChange={() => setPromptMarkdownPreview(!promptMarkdownPreview)}
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full  peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                    <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      {promptMarkdownPreview ? (
                        <>
                          <FiEye className="w-4 h-4" />
                          {isRtl ? "معاينة" : "Preview"}
                        </>
                      ) : (
                        <>
                          <FiEdit3 className="w-4 h-4" />
                          {isRtl ? "تحرير" : "Edit"}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Prompt Input/Preview */}
              <div className="relative">
                {!promptMarkdownPreview ? (
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={isRtl ? "أضف وصفًا تفصيليًا هنا..." : "Add your detailed prompt here..."}
                    className={`w-full h-48 px-3 py-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 bg-white dark:bg-gray-800 transition-colors duration-200 resize-none ${isRtl ? 'text-right' : 'text-left'}`}
                    disabled={activeSection !== 'prompt'}
                  />
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/50 overflow-auto">
                    {prompt.trim() ? (
                      <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {prompt}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">
                        {isRtl ? "لا يوجد محتوى للمعاينة" : "No content to preview"}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {activeSection === 'prompt' && !promptComplete && (
                <div className="flex justify-end">
                  <button
                    onClick={savePrompt}
                    disabled={!validatePrompt()}
                    className={`flex items-center px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 ${
                      validatePrompt() 
                      ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 shadow-sm' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    }`}
                  >
                    {isRtl ? "حفظ ومتابعة" : "Save & Continue"}
                    <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </SectionCard>
          
          {/* Response Section */}
          <SectionCard 
            title={isRtl ? "الاستجابة المتوقعة" : "Expected Response"}
            isComplete={responseComplete}
            isActive={activeSection === 'response'}
            onActivate={() => setActiveSection('response')}
            onSave={saveResponse}
            canSave={validateResponse()}
            hasChanges={responseHasChanges}
            isRtl={isRtl}
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isRtl 
                    ? <>قدم الاستجابة المتوقعة <span className="text-sm text-red-500">(الحد الأدنى 20 كلمة)</span></> 
                    : <>Provide the expected response <span className="text-sm text-red-500">(minimum 20 words)</span></>
                  }
                </label>
                <div className="flex items-center gap-4">
                  <WordCounter text={response} minWords={20} />
                  {/* Response Markdown Preview Toggle */}
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={responseMarkdownPreview}
                        onChange={() => setResponseMarkdownPreview(!responseMarkdownPreview)}
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                    <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      {responseMarkdownPreview ? (
                        <>
                          <FiEye className="w-4 h-4" />
                          {isRtl ? "معاينة" : "Preview"}
                        </>
                      ) : (
                        <>
                          <FiEdit3 className="w-4 h-4" />
                          {isRtl ? "تحرير" : "Edit"}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Response Input/Preview */}
              <div className="relative">
                {!responseMarkdownPreview ? (
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder={isRtl ? "أضف الإجابة المتوقعة هنا..." : "Add your expected response here..."}
                    className={`w-full h-48 px-3 py-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 bg-white dark:bg-gray-800 transition-colors duration-200 resize-none ${isRtl ? 'text-right' : 'text-left'}`}
                    disabled={activeSection !== 'response'}
                  />
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/50 overflow-auto">
                    {response.trim() ? (
                      <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {response}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">
                        {isRtl ? "لا يوجد محتوى للمعاينة" : "No content to preview"}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {activeSection === 'response' && !responseComplete && (
                <div className="flex justify-end">
                  <button
                    onClick={saveResponse}
                    disabled={!validateResponse()}
                    className={`flex items-center px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 ${
                      validateResponse() 
                      ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 shadow-sm' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    }`}
                  >
                    {isRtl ? "حفظ ومتابعة" : "Save & Continue"}
                    <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </SectionCard>
          
          {/* Metadata Section */}
          <SectionCard 
            title={isRtl ? "تفاصيل المهمة" : "Task Details"}
            isComplete={metadataComplete}
            isActive={activeSection === 'metadata'}
            onActivate={() => setActiveSection('metadata')}
            onSave={saveMetadata}
            canSave={validateMetadata()}
            hasChanges={metadataHasChanges}
            isRtl={isRtl}
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {isRtl ? "لغة البرمجة" : "Programming Language"}
                  </label>
                  <div className="relative">
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      disabled={activeSection !== 'metadata'}
                      className={`block w-full px-4 py-2.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 ${language ? '' : 'text-gray-500 dark:text-gray-400'}`}
                    >
                      <option value="" disabled>{isRtl ? "اختر لغة البرمجة" : "Select a programming language"}</option>
                      {languageOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {isRtl ? "فئة المهمة" : "Task Category"}
                  </label>
                  <div className="relative">
                    <select
                      value={taskCategory}
                      onChange={(e) => setTaskCategory(e.target.value)}
                      disabled={activeSection !== 'metadata'}
                      className={`block w-full px-4 py-2.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 ${taskCategory ? '' : 'text-gray-500 dark:text-gray-400'}`}
                    >
                      <option value="" disabled>{isRtl ? "اختر فئة" : "Select a category"}</option>
                      {taskCategoryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {isRtl ? "الفئة التقنية" : "Technical Category"}
                </label>
                <div className="relative">
                  <select
                    value={technicalCategory}
                    onChange={(e) => setTechnicalCategory(e.target.value)}
                    disabled={activeSection !== 'metadata'}
                    className={`block w-full px-4 py-2.5 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-600 ${technicalCategory ? '' : 'text-gray-500 dark:text-gray-400'}`}
                  >
                    <option value="" disabled>{isRtl ? "اختر فئة تقنية" : "Select a technical category"}</option>
                    {technicalCategoryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.display}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {isRtl 
                    ? "اختر نوع المهمة البرمجية"
                    : "Choose the type of coding task"
                  }
                </p>
              </div>
              
              {/* Arabic MSA Checkbox */}
              <div className="mt-4">
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="arabic-msa-toggle"
                    checked={isArabicMSA}
                    onChange={() => setIsArabicMSA(!isArabicMSA)}
                    className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 dark:focus:ring-cyan-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="arabic-msa-toggle" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {isRtl ? "اللغة العربية" : "Arabic"}
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {isRtl 
                    ? "حدد هذا الخيار إذا كانت المهمة تستخدم اللغة العربية"
                    : "Check this if the task uses Arabic language"
                  }
                </p>
              </div>
              
              {activeSection === 'metadata' && !metadataComplete && (
                <div className="flex justify-end">
                  <button
                    onClick={saveMetadata}
                    disabled={!validateMetadata()}
                    className={`flex items-center px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 ${
                      validateMetadata() 
                      ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 shadow-sm' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    }`}
                  >
                    {isRtl ? "حفظ التفاصيل" : "Save Details"}
                    <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </SectionCard>
          
          {/* Task Summary & Submit Section */}
          {promptComplete && responseComplete && metadataComplete && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  {isRtl ? "ملخص المهمة" : "Task Summary"}
                </h2>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{isRtl ? "لغة البرمجة" : "Programming Language"}</h3>
                    <p className="text-gray-800 dark:text-gray-200 font-medium">{languageOptions.find(l => l.value === language)?.display || language}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{isRtl ? "فئة المهمة" : "Task Category"}</h3>
                    <p className="text-gray-800 dark:text-gray-200 font-medium">{taskCategoryOptions.find(c => c.value === taskCategory)?.display || taskCategory}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{isRtl ? "الفئة التقنية" : "Technical Category"}</h3>
                  <p className="text-gray-800 dark:text-gray-200 font-medium">{technicalCategoryOptions.find(c => c.value === technicalCategory)?.display || technicalCategory}</p>
                </div>
                
                {/* Full height preview sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{isRtl ? "نص المهمة" : "Task Prompt"}</h3>
                    <div className="h-64 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto">
                      <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {prompt}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{isRtl ? "الاستجابة المتوقعة" : "Expected Response"}</h3>
                    <div className="h-64 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-auto">
                      <div className={`markdown-preview text-gray-800 dark:text-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {response}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>
                
                {isArabicMSA && (
                  <div className="flex items-center">
                    <div className="px-3 py-1 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-xs font-medium rounded-full border border-cyan-200 dark:border-cyan-700">
                      {isRtl ? "اللغة العربية" : "Arabic"}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setPrompt("");
                      setPromptSaved("");
                      setPromptComplete(false);
                      setResponse("");
                      setResponseSaved("");
                      setResponseComplete(false);
                      setLanguage("");
                      setLanguageSaved("");
                      setTaskCategory("");
                      setTaskCategorySaved("");
                      setTechnicalCategory("");
                      setTechnicalCategorySaved("");
                      setMetadataComplete(false);
                      setIsArabicMSA(false);
                      setCurrentStep(0);
                      setActiveSection('prompt');
                      setPromptMarkdownPreview(false);
                      setResponseMarkdownPreview(false);
                      localStorage.removeItem(`task_draft`);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 dark:focus:ring-offset-gray-900 transition-all duration-200"
                  >
                    {isRtl ? "إعادة تعيين المهمة" : "Reset Task"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </TasksLayout>
  );
};

export default AttempterTaskDetails;